import { Field } from 'src/models';
import { Box, Alert, Typography } from '@mui/material';
import { forceString } from 'src/utils/forceString';
import logger from 'src/log/log';

export const Video = ({ field }: { field: Field }) => {
  const videoData = (() => {
    const dataSource = (field as any).properties?.dataSource || (field as any).dataSource;
    if (!dataSource) return { url: '', type: 'youtube' };

    const dataSourceStr = forceString(dataSource);

    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(dataSourceStr);
      return parsed && typeof parsed === 'object' ? parsed : { url: dataSourceStr, type: 'youtube' };
    } catch {
      // If parsing fails, treat as plain URL string
      return { url: dataSourceStr, type: 'youtube' };
    }
  })();

  const getEmbedUrl = (url: string) => {
    try {
      if (!url || !url.trim()) return '';

      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        const videoId = url.includes('youtu.be')
          ? url.split('/').pop()?.split('?')[0]
          : new URLSearchParams(new URL(url).search).get('v');

        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }

      if (url.includes('vimeo.com')) {
        const videoId = url.split('/').pop()?.split('?')[0];
        if (videoId && /^\d+$/.test(videoId)) {
          return `https://player.vimeo.com/video/${videoId}`;
        }
      }

      if (url.includes('loom.com')) {
        // Enhanced Loom URL handling for the reported bug
        const match = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
        const videoId = match ? match[1] : '';
        if (videoId) {
          return `https://www.loom.com/embed/${videoId}`;
        }
      }

      // For other URLs, try to use them directly if they look like video embeds
      if (url.includes('/embed/') || url.includes('player.')) {
        return url;
      }

      return '';
    } catch (error) {
      logger.error('Error processing video URL:', { url, error });
      return '';
    }
  };

  const embedUrl = getEmbedUrl(videoData.url);

  // If no valid embed URL, show message
  if (!embedUrl) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          <Typography variant="body2">
            {videoData.url ? 'Unable to embed this video URL. Please check the URL format.' : 'No video URL provided.'}
          </Typography>
          {videoData.url && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              URL: {videoData.url}
            </Typography>
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        paddingTop: '56.25%', // 16:9 Aspect Ratio
        width: '100%',
      }}
    >
      <iframe
        title="Video player"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
        src={embedUrl}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </Box>
  );
};
